import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  LinearProgress,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Visibility as PreviewIcon,
  AutoAwesome as AIIcon,
  ExpandMore as ExpandIcon,
  Lock as SystemIcon,
  DragHandle as DragIcon,
  UploadFile as UploadIcon,
  CheckCircle as CheckIcon,
  Description as DocIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  SubdirectoryArrowRight as ChildIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import api from '../../utils/api';
import { withOfflineFallback, skipWhenOffline, readSoapTemplates, OfflineNotice, useIsOffline } from '../../offline';
import TemplateFormRenderer, { TemplateFieldControl } from '../../components/SoapNote/TemplateFormRenderer';
import DentalChart from '../../components/SoapNote/DentalChart';
import SubjectiveSection from '../../components/SoapNote/SubjectiveSection';
import ObjectiveSection from '../../components/SoapNote/ObjectiveSection';
import AssessmentSection from '../../components/SoapNote/AssessmentSection';
import PlanSection from '../../components/SoapNote/PlanSection';
import CommaListInput from '../../components/CommaListInput';

const EXAM_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'ppe', label: 'Pre-Purchase (PPE)' },
  { value: 'dental', label: 'Dental' },
  { value: 'lameness', label: 'Lameness' },
  { value: 'colic', label: 'Colic' },
  { value: 'neonatal', label: 'Neonatal' },
  { value: 'reproductive', label: 'Reproductive' },
];

const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'boolean', label: 'Yes/No toggle' },
  { value: 'file_drop', label: 'File upload' },
  { value: 'visual', label: 'Visual (dental chart)' },
  { value: 'group', label: 'Group (sub-fields)' },
  { value: 'treatment_plan', label: 'Full Plan' },
];

const SECTIONS = ['subjective', 'objective', 'assessment', 'plan'] as const;

interface SoapTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  examType?: string;
  isDefault: boolean;
  isSystemDefault: boolean;
  schema?: TemplateFieldControl[];
  // Legacy JSON blobs kept for backward compatibility with older pre-schema templates
  subjective?: any;
  objective?: any;
  assessment?: any;
  plan?: any;
}

/**
 * Legacy templates (pre-schema) stored structure in `subjective/objective/assessment/plan`
 * JSON blobs rather than a schema array. This converts those blobs into a best-effort
 * schema so they can be previewed/cloned in the template editor.
 */
const titleCase = (s: string): string =>
  s.replace(/([A-Z])/g, ' $1').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
    .replace(/^\w/, c => c.toUpperCase());

export const legacyToSchema = (t: {
  subjective?: any;
  objective?: any;
  assessment?: any;
  plan?: any;
}): TemplateFieldControl[] => {
  const schema: TemplateFieldControl[] = [];
  const used = new Set<string>();
  const slug = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40) || 'field';
  const nextKey = (base: string) => {
    let k = slug(base); let i = 2;
    while (used.has(k)) { k = `${slug(base)}_${i++}`; }
    used.add(k);
    return k;
  };

  // subjective.prompts → textarea per prompt
  const prompts = t.subjective?.prompts;
  if (Array.isArray(prompts)) {
    prompts.forEach((p: string) => schema.push({
      key: nextKey(p), label: String(p), type: 'textarea', section: 'subjective', required: false,
    }));
  }

  // objective.vitals → number per vital
  const vitals = t.objective?.vitals;
  if (vitals && typeof vitals === 'object' && !Array.isArray(vitals)) {
    Object.entries(vitals).forEach(([name, meta]: [string, any]) => {
      schema.push({
        key: nextKey(name),
        label: titleCase(name),
        type: 'number',
        section: 'objective',
        required: false,
        unit: meta?.unit,
        placeholder: meta?.normal ? `Normal: ${meta.normal}` : undefined,
      });
    });
  }

  // objective.physicalExam → textarea per system (items become placeholder hint)
  const pe = t.objective?.physicalExam;
  if (pe && typeof pe === 'object' && !Array.isArray(pe)) {
    Object.entries(pe).forEach(([system, items]: [string, any]) => {
      schema.push({
        key: nextKey(system),
        label: titleCase(system),
        type: 'textarea',
        section: 'objective',
        required: false,
        placeholder: Array.isArray(items) ? items.join(', ') : undefined,
      });
    });
  }

  // objective.lamenessEval → textarea per subsection
  const le = t.objective?.lamenessEval;
  if (le && typeof le === 'object' && !Array.isArray(le)) {
    Object.entries(le).forEach(([sub, val]: [string, any]) => {
      if (sub === 'gradeScale') return;
      const placeholder = Array.isArray(val)
        ? val.join(', ')
        : (val && typeof val === 'object' ? Object.keys(val).join(', ') : undefined);
      schema.push({
        key: nextKey(sub),
        label: titleCase(sub),
        type: 'textarea',
        section: 'objective',
        required: false,
        placeholder,
      });
    });
  }

  // assessment.sections / plan.sections → textarea per section
  (['assessment', 'plan'] as const).forEach(section => {
    const sections = (t as any)[section]?.sections;
    if (Array.isArray(sections)) {
      sections.forEach((s: string) => schema.push({
        key: nextKey(s), label: String(s), type: 'textarea', section, required: false,
      }));
    }
  });

  // Every legacy note shows the Plan block, so its copy keeps it.
  schema.push({
    key: nextKey('treatment_plan'), label: 'Full Plan', type: 'treatment_plan', section: 'plan', required: false,
  });

  return schema;
};

/** Return the template's schema, falling back to a legacy-converted one if schema is empty. */
const resolveSchema = (t: SoapTemplate | null | undefined): TemplateFieldControl[] => {
  if (!t) return [];
  if (Array.isArray(t.schema) && t.schema.length > 0) return t.schema as TemplateFieldControl[];
  return legacyToSchema(t);
};

/** A built-in with no fields of its own is the standard four-section note (General Exam): the default. */
const isDefaultLayout = (t: SoapTemplate): boolean =>
  !!t.isSystemDefault && !(Array.isArray(t.schema) && t.schema.length > 0);

const SECTION_COLORS: Record<string, string> = {
  subjective: '#1976d2',
  objective: '#388e3c',
  assessment: '#f57c00',
  plan: '#7b1fa2',
};

// Child field types allowed inside groups (no nested groups or visuals)
const CHILD_FIELD_TYPES = FIELD_TYPES.filter(ft => !['group', 'visual', 'file_drop', 'treatment_plan'].includes(ft.value));

interface FieldEditorProps {
  field: Partial<TemplateFieldControl>;
  onChange: (field: Partial<TemplateFieldControl>) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  depth?: number;  // 0 = top-level, 1 = inside group
  planBlockElsewhere?: boolean;  // another field is already the Full Plan block
}

const FieldEditor: React.FC<FieldEditorProps> = ({ field, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, depth = 0, planBlockElsewhere }) => {
  const sectionColor = SECTION_COLORS[field.section || 'subjective'] || '#999';
  const isGroup = field.type === 'group';
  const isVisual = field.type === 'visual';
  const isPlanBlock = field.type === 'treatment_plan';
  const children = (field as any).children as Partial<TemplateFieldControl>[] | undefined;

  const addChild = () => {
    const currentChildren = children || [];
    const newChild: TemplateFieldControl = {
      key: `${field.key || 'group'}_field_${currentChildren.length + 1}`,
      label: '',
      type: 'text' as const,
      section: (field.section || 'subjective') as 'subjective',
      required: false,
    };
    onChange({
      ...field,
      children: [...currentChildren, newChild] as TemplateFieldControl[],
    });
  };

  const updateChild = (index: number, child: Partial<TemplateFieldControl>) => {
    const updated = [...(children || [])] as TemplateFieldControl[];
    updated[index] = { ...child, section: field.section } as TemplateFieldControl; // children inherit parent section
    onChange({ ...field, children: updated });
  };

  const removeChild = (index: number) => {
    onChange({ ...field, children: (children || []).filter((_, i) => i !== index) as TemplateFieldControl[] });
  };

  const moveChild = (index: number, direction: -1 | 1) => {
    const arr = [...(children || [])] as TemplateFieldControl[];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= arr.length) return;
    [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
    onChange({ ...field, children: arr });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1,
        borderLeft: depth === 0 ? `3px solid ${sectionColor}` : '1px solid',
        borderLeftColor: depth === 0 ? sectionColor : 'divider',
        ml: depth > 0 ? 3 : 0,
        bgcolor: depth > 0 ? 'grey.50' : 'background.paper',
      }}
    >
      <Box display="flex" gap={1} alignItems="flex-start">
        {/* Reorder buttons */}
        <Box display="flex" flexDirection="column" sx={{ flexShrink: 0, mt: 0.5 }}>
          <IconButton size="small" disabled={isFirst} onClick={onMoveUp} sx={{ p: 0.25 }}>
            <MoveUpIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" disabled={isLast} onClick={onMoveDown} sx={{ p: 0.25 }}>
            <MoveDownIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box flex={1} display="flex" gap={1} flexWrap="wrap">
          {!isPlanBlock && (
            <>
              <TextField
                label="Key (unique ID)"
                size="small"
                value={field.key || ''}
                onChange={e => onChange({ ...field, key: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                sx={{ width: 150 }}
              />
              <TextField
                label="Label"
                size="small"
                value={field.label || ''}
                onChange={e => onChange({ ...field, label: e.target.value })}
                sx={{ flex: 1, minWidth: 140 }}
              />
            </>
          )}
          <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={field.type || 'text'}
              label="Type"
              onChange={e => {
                const newType = e.target.value as TemplateFieldControl['type'];
                const cleaned: Partial<TemplateFieldControl> = { ...field, type: newType };
                if (newType !== 'select' && newType !== 'checklist') delete cleaned.options;
                if (newType !== 'number') delete cleaned.unit;
                if (newType !== 'visual') delete cleaned.visualType;
                if (newType !== 'group') delete (cleaned as any).children;
                if (newType !== 'file_drop') { delete cleaned.accept; delete cleaned.multiple; }
                if (newType !== 'textarea') delete cleaned.placeholder;
                if (newType === 'visual') cleaned.visualType = 'dental_chart';
                if (newType === 'group') (cleaned as any).children = [];
                // The block is the note's Plan: it lives in the Plan section and has nothing to require.
                if (newType === 'treatment_plan') {
                  cleaned.section = 'plan';
                  cleaned.required = false;
                  // Nothing shows these, but a saved field needs both.
                  cleaned.label = 'Full Plan';
                  if (!cleaned.key) cleaned.key = 'full_plan';
                }
                onChange(cleaned);
              }}
            >
              {(depth > 0 ? CHILD_FIELD_TYPES : FIELD_TYPES).map(ft => (
                <MenuItem
                  key={ft.value}
                  value={ft.value}
                  disabled={ft.value === 'treatment_plan' && planBlockElsewhere}
                >
                  {ft.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {depth === 0 && !isPlanBlock && (
            <FormControl size="small" sx={{ width: 120 }}>
              <InputLabel>Section</InputLabel>
              <Select
                value={field.section || 'subjective'}
                label="Section"
                onChange={e => onChange({ ...field, section: e.target.value as any })}
              >
                {SECTIONS.map(s => (
                  <MenuItem key={s} value={s}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SECTION_COLORS[s] }} />
                      {s}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* Required toggle */}
          {!isGroup && !isPlanBlock && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!field.required}
                  onChange={e => onChange({ ...field, required: e.target.checked })}
                />
              }
              label={<Typography variant="caption">Required</Typography>}
              sx={{ ml: 0, mr: 0 }}
            />
          )}
        </Box>
        <IconButton size="small" color="error" onClick={onDelete}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Extra options per type */}
      {(field.type === 'select' || field.type === 'checklist') && (
        <Box mt={1} ml={4}>
          {/* Raw-draft input: a value derived from the parsed array ate the
              trailing comma on the same keystroke (see CommaListInput). */}
          <CommaListInput
            label="Options (comma-separated)"
            size="small"
            fullWidth
            value={field.options}
            onChange={options => onChange({ ...field, options })}
            placeholder="Option 1, Option 2, Option 3"
            helperText="Separate options with commas"
          />
        </Box>
      )}
      {field.type === 'number' && (
        <Box mt={1} ml={4}>
          <TextField
            label="Unit (e.g. °F, bpm)"
            size="small"
            value={field.unit || ''}
            onChange={e => onChange({ ...field, unit: e.target.value })}
            sx={{ width: 160 }}
          />
        </Box>
      )}
      {field.type === 'textarea' && (
        <Box mt={1} ml={4}>
          <TextField
            label="Placeholder text"
            size="small"
            value={field.placeholder || ''}
            onChange={e => onChange({ ...field, placeholder: e.target.value })}
            sx={{ width: 300 }}
          />
        </Box>
      )}

      {isPlanBlock && (
        <Box mt={1} ml={4}>
          <Typography variant="caption" color="text.secondary">
            Adds the note's full Plan: diagnostics, treatments and medications from your price list, procedures, a
            follow-up date and plan notes. It works the same as on a note without a template, including recording
            vaccines with their lot when the note is completed.
          </Typography>
        </Box>
      )}

      {/* Visual type — show config + mini preview */}
      {isVisual && (
        <Box mt={1.5} ml={4}>
          <Box display="flex" gap={2} alignItems="center" mb={1.5}>
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Visual type</InputLabel>
              <Select
                value={(field as any).visualType || 'dental_chart'}
                label="Visual type"
                onChange={e => onChange({ ...field, visualType: e.target.value as any })}
              >
                <MenuItem value="dental_chart">Dental Chart</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Interactive equine dental chart using Triadan numbering
            </Typography>
          </Box>
          {/* Mini dental chart preview */}
          {((field as any).visualType || 'dental_chart') === 'dental_chart' && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', maxWidth: 520 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Preview (read-only):
              </Typography>
              <Box sx={{ transform: 'scale(0.85)', transformOrigin: 'top left' }}>
                <DentalChart value={{}} readOnly />
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* File drop config */}
      {field.type === 'file_drop' && (
        <Box mt={1} ml={4} display="flex" gap={1}>
          <TextField
            label="Accept (e.g. image/*,.pdf)"
            size="small"
            value={field.accept || '*'}
            onChange={e => onChange({ ...field, accept: e.target.value })}
            sx={{ width: 200 }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!!(field as any).multiple}
                onChange={e => onChange({ ...field, multiple: e.target.checked })}
              />
            }
            label={<Typography variant="caption">Multiple files</Typography>}
          />
        </Box>
      )}

      {/* Group children */}
      {isGroup && (
        <Box mt={1.5} ml={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <ChildIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Sub-fields ({(children || []).length})
            </Typography>
          </Box>
          {(children || []).map((child, ci) => (
            <FieldEditor
              key={ci}
              field={child}
              onChange={c => updateChild(ci, c)}
              onDelete={() => removeChild(ci)}
              onMoveUp={() => moveChild(ci, -1)}
              onMoveDown={() => moveChild(ci, 1)}
              isFirst={ci === 0}
              isLast={ci === (children || []).length - 1}
              depth={1}
            />
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addChild}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, ml: 3 }}
          >
            Add Sub-field
          </Button>
        </Box>
      )}
    </Paper>
  );
};

const TemplateEditor: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  // Template writes aren't in the /sync push contract yet — block them offline.
  const isOffline = useIsOffline();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<SoapTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SoapTemplate | null>(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [aiExamType, setAiExamType] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  // Document-drop state
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docDragging, setDocDragging] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [docUploadProgress, setDocUploadProgress] = useState(0);
  const [docExtracted, setDocExtracted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'general',
    examType: 'general',
    schema: [] as Partial<TemplateFieldControl>[],
  });

  const { data: templates = [], isLoading } = useQuery<SoapTemplate[]>(
    ['soap-templates'],
    withOfflineFallback(
      () => api.get('/soap-notes/templates').then(r => r.data),
      // Replica rows carry isSystemDefault/isDefault, so the system/clinic split works.
      () => readSoapTemplates() as Promise<SoapTemplate[]>,
    ),
  );

  const createMutation = useMutation(
    (data: any) => api.post('/soap-notes/templates', data).then(r => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['soap-templates']);
        enqueueSnackbar('Template created', { variant: 'success' });
        setCreateDialogOpen(false);
        resetForm();
      },
      onError: () => { enqueueSnackbar('Failed to create template', { variant: 'error' }); },
    },
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => api.patch(`/soap-notes/templates/${id}`, data).then(r => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['soap-templates']);
        enqueueSnackbar('Template updated', { variant: 'success' });
        setEditDialogOpen(false);
      },
      onError: () => { enqueueSnackbar('Failed to update template', { variant: 'error' }); },
    },
  );

  const deleteMutation = useMutation(
    (id: string) => api.delete(`/soap-notes/templates/${id}`).then(r => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['soap-templates']);
        enqueueSnackbar('Template deleted', { variant: 'success' });
      },
      onError: () => { enqueueSnackbar('Failed to delete template', { variant: 'error' }); },
    },
  );

  const resetForm = () => setForm({ name: '', description: '', category: 'general', examType: 'general', schema: [] });

  const handleEdit = (t: SoapTemplate) => {
    setSelectedTemplate(t);
    setForm({
      name: t.name,
      description: t.description || '',
      category: t.category,
      examType: t.examType || 'general',
      schema: resolveSchema(t) as Partial<TemplateFieldControl>[],
    });
    setDialogTab(0);
    setPreviewValues({});
    setEditDialogOpen(true);
  };

  const handleClone = (t: SoapTemplate) => {
    setForm({
      name: `${t.name} (Copy)`,
      description: t.description || '',
      category: t.category,
      examType: t.examType || 'general',
      schema: resolveSchema(t) as Partial<TemplateFieldControl>[],
    });
    setDialogTab(0);
    setPreviewValues({});
    setCreateDialogOpen(true);
  };

  const handleAIGenerate = async () => {
    if (!aiDescription.trim()) return;
    setAiGenerating(true);
    try {
      const response = await api.post('/ai/generate-template', {
        description: aiDescription,
        examType: aiExamType || undefined,
      });
      const generated = response.data;
      setForm({
        name: generated.name || '',
        description: generated.description || '',
        category: generated.category || 'general',
        examType: generated.examType || aiExamType || 'general',
        schema: generated.schema || [],
      });
      setAiDialogOpen(false);
      resetAiDialog();
      setCreateDialogOpen(true);
      enqueueSnackbar('Template generated by AI — review and save', { variant: 'success' });
    } catch {
      enqueueSnackbar('AI generation failed', { variant: 'error' });
    } finally {
      setAiGenerating(false);
    }
  };

  const resetAiDialog = () => {
    setAiDescription('');
    setAiExamType('');
    setDocFile(null);
    setDocExtracted(false);
    setDocUploadProgress(0);
  };

  const handleDocDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDocDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleDocFileSelect(file);
  }, []); // eslint-disable-line

  const handleDocFileSelect = async (file: File) => {
    // Call-time check (not render-captured isOffline): the drag-drop path runs
    // through a stale useCallback closure, so a hook value here could be stale.
    if (!skipWhenOffline()) {
      enqueueSnackbar('AI template generation needs an internet connection.', { variant: 'warning' });
      return;
    }
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      enqueueSnackbar('Unsupported file type. Use PDF, PNG, JPG, WEBP, or DOCX.', { variant: 'warning' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      enqueueSnackbar('File too large (max 20 MB)', { variant: 'warning' });
      return;
    }
    setDocFile(file);
    setDocExtracted(false);
    setDocUploading(true);
    setDocUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress while uploading
      const progressInterval = setInterval(() => {
        setDocUploadProgress(p => Math.min(p + 15, 85));
      }, 600);

      const response = await api.post('/ai/generate-template-from-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      clearInterval(progressInterval);
      setDocUploadProgress(100);

      const generated = response.data;
      // Pre-fill the description + examType from what AI found
      setAiDescription(generated.description || `Template extracted from ${file.name}`);
      setAiExamType(generated.examType || '');
      setDocExtracted(true);

      // Directly open create dialog with full schema
      setForm({
        name: generated.name || '',
        description: generated.description || '',
        category: generated.category || 'general',
        examType: generated.examType || 'general',
        schema: generated.schema || [],
      });
      setAiDialogOpen(false);
      resetAiDialog();
      setCreateDialogOpen(true);
      enqueueSnackbar(
        `AI extracted ${generated.schema?.length || 0} fields from "${file.name}" — review and save`,
        { variant: 'success' },
      );
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to extract template from document', { variant: 'error' });
      setDocFile(null);
      setDocUploadProgress(0);
    } finally {
      setDocUploading(false);
    }
  };

  // Dialog tab state
  const [dialogTab, setDialogTab] = useState(0);

  // Preview values for live preview (inside edit dialog tab)
  const [previewValues, setPreviewValues] = useState<Record<string, any>>({});
  // Preview values for the standalone Preview dialog
  const [standalonePreviewValues, setStandalonePreviewValues] = useState<Record<string, any>>({});

  const addField = () => {
    setForm(f => {
      // Pick an auto-key that doesn't collide with any existing top-level OR child key.
      const existing = new Set<string>();
      f.schema.forEach(fld => {
        if (fld.key) existing.add(fld.key);
        ((fld as any).children || []).forEach((c: any) => { if (c.key) existing.add(c.key); });
      });
      let n = f.schema.length + 1;
      while (existing.has(`field_${n}`)) n++;
      return {
        ...f,
        schema: [...f.schema, { key: `field_${n}`, label: '', type: 'text', section: 'subjective', required: false }],
      };
    });
  };

  const updateField = (index: number, field: Partial<TemplateFieldControl>) => {
    setForm(f => ({ ...f, schema: f.schema.map((fld, i) => i === index ? field : fld) }));
  };

  const removeField = (index: number) => {
    setForm(f => ({ ...f, schema: f.schema.filter((_, i) => i !== index) }));
  };

  const moveField = (index: number, direction: -1 | 1) => {
    setForm(f => {
      const arr = [...f.schema];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= arr.length) return f;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return { ...f, schema: arr };
    });
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      enqueueSnackbar('Template name is required', { variant: 'warning' });
      return;
    }
    const payload = { ...form, schema: form.schema.filter(f => f.key && f.label) };
    if (editDialogOpen && selectedTemplate) {
      updateMutation.mutate({ id: selectedTemplate.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  // Compute section field counts for the editor summary chips
  const sectionCounts = useMemo(() => {
    const counts: Record<string, number> = { subjective: 0, objective: 0, assessment: 0, plan: 0 };
    form.schema.forEach(f => {
      const sec = f.section || 'subjective';
      if (counts[sec] !== undefined) {
        counts[sec]++;
        // Count group children too
        if (f.type === 'group' && (f as any).children) {
          counts[sec] += ((f as any).children as any[]).length;
        }
      }
    });
    return counts;
  }, [form.schema]);

  // Valid schema for preview (filter out incomplete fields)
  const validSchema = useMemo(() =>
    form.schema.filter(f => f.key && f.label) as TemplateFieldControl[],
    [form.schema],
  );
  // What a Full Plan field shows on a note, empty and read-only.
  const planBlockPreview = <PlanSection data={{}} onChange={() => {}} disabled />;

  // The default (General Exam) leads the built-ins; the rest keep the server's order.
  const systemTemplates = templates
    .filter(t => t.isSystemDefault)
    .sort((a, b) => Number(isDefaultLayout(b)) - Number(isDefaultLayout(a)));
  const clinicTemplates = templates.filter(t => !t.isSystemDefault);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">SOAP Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage structured examination templates for clinical records
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            onClick={() => setAiDialogOpen(true)}
            disabled={isOffline}
          >
            Generate with AI
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setCreateDialogOpen(true); }} disabled={isOffline}>
            New Template
          </Button>
        </Box>
      </Box>

      <OfflineNotice>
        Template changes need an internet connection — you can browse and preview templates offline.
      </OfflineNotice>

      {/* System Templates */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <SystemIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>System Templates</Typography>
            <Chip label={systemTemplates.length} size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Alert severity="info" sx={{ mx: 2, mb: 1, mt: 0 }}>
            System templates are built-in and read-only. Clone them to create customized versions.
          </Alert>
          <List disablePadding>
            {systemTemplates.map(t => (
              <ListItem key={t.id} divider sx={{ px: 2 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      {t.name}
                      {t.examType && <Chip label={t.examType} size="small" variant="outlined" />}
                      {(() => { const n = resolveSchema(t).length; return n > 0 ? <Chip label={`${n} fields`} size="small" color="primary" variant="outlined" /> : null; })()}
                      {isDefaultLayout(t) && <Chip label="Default" size="small" color="success" />}
                    </Box>
                  }
                  secondary={t.description}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Preview">
                    <IconButton size="small" onClick={() => setPreviewTemplate(t)}>
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clone to my templates">
                    <IconButton size="small" onClick={() => handleClone(t)}>
                      <CloneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Clinic Templates */}
      <Accordion defaultExpanded sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>My Templates</Typography>
            <Chip label={clinicTemplates.length} size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {clinicTemplates.length === 0 ? (
            <Box py={3} textAlign="center">
              <Typography color="text.secondary">
                No custom templates yet. Create one or clone a system template.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {clinicTemplates.map(t => (
                <ListItem key={t.id} divider sx={{ px: 2 }}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        {t.name}
                        {t.examType && <Chip label={t.examType} size="small" variant="outlined" />}
                        {(() => { const n = resolveSchema(t).length; return n > 0 ? <Chip label={`${n} fields`} size="small" color="primary" variant="outlined" /> : null; })()}
                        {t.isDefault && <Chip label="Default" size="small" color="success" />}
                      </Box>
                    }
                    secondary={t.description}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Preview">
                      <IconButton size="small" onClick={() => setPreviewTemplate(t)}>
                        <PreviewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* Default templates (isDefault: true) are shared/read-only — backend
                        rejects updates. Show Clone instead of Edit. */}
                    {t.isDefault ? (
                      <Tooltip title="Clone to customize (default template — edit not allowed)">
                        <IconButton size="small" onClick={() => handleClone(t)}>
                          <CloneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton size="small" onClick={() => handleEdit(t)} disabled={isOffline}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deleteMutation.isLoading || isOffline}
                              onClick={() => {
                                if (window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) {
                                  deleteMutation.mutate(t.id);
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
          setSelectedTemplate(null);
          setDialogTab(0);
          setPreviewValues({});
          resetForm();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editDialogOpen ? `Edit: ${selectedTemplate?.name}` : 'New Template'}
            </Typography>
            <Box display="flex" gap={0.5}>
              {SECTIONS.map(s => (
                <Chip
                  key={s}
                  label={`${s}: ${sectionCounts[s]}`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: SECTION_COLORS[s], color: SECTION_COLORS[s], fontSize: '11px' }}
                />
              ))}
            </Box>
          </Box>
          <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ mt: 1 }}>
            <Tab label="Edit Fields" />
            <Tab label="Live Preview" disabled={validSchema.length === 0} />
          </Tabs>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 500 }}>
          {/* Covers connectivity dropping mid-edit — Save below is also disabled. */}
          <OfflineNotice>
            Template changes need an internet connection — your draft will be lost if you close this dialog.
          </OfflineNotice>
          {dialogTab === 0 ? (
            <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
              <Box display="flex" gap={2}>
                <TextField label="Template Name *" fullWidth size="small" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Exam Type</InputLabel>
                  <Select value={form.examType} label="Exam Type" onChange={e => setForm(f => ({ ...f, examType: e.target.value, category: e.target.value }))}>
                    {EXAM_TYPES.map(et => <MenuItem key={et.value} value={et.value}>{et.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <TextField label="Description" fullWidth size="small" multiline rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />

              <Divider>
                <Chip label={`${form.schema.length} top-level fields`} size="small" />
              </Divider>

              {form.schema.length === 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No fields yet. Click "Add Field" to start building your template, or use "Generate with AI" to auto-create fields from a description.
                </Alert>
              )}

              <Box>
                {form.schema.map((field, i) => (
                  <FieldEditor
                    key={i}
                    field={field}
                    onChange={f => updateField(i, f)}
                    onDelete={() => removeField(i)}
                    onMoveUp={() => moveField(i, -1)}
                    onMoveDown={() => moveField(i, 1)}
                    isFirst={i === 0}
                    isLast={i === form.schema.length - 1}
                    planBlockElsewhere={form.schema.some((f, j) => j !== i && f.type === 'treatment_plan')}
                  />
                ))}
                <Button startIcon={<AddIcon />} onClick={addField} size="small" variant="outlined" sx={{ mt: 0.5 }}>
                  Add Field
                </Button>
              </Box>
            </Box>
          ) : (
            <Box mt={1}>
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                This is a live preview of how the template will look when filling out a SOAP note. Fields with missing key or label are hidden.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2.5, maxWidth: 800, mx: 'auto' }}>
                <TemplateFormRenderer
                  schema={validSchema}
                  values={previewValues}
                  onChange={(key, val) => setPreviewValues(prev => ({ ...prev, [key]: val }))}
                  treatmentPlan={planBlockPreview}
                />
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
            setSelectedTemplate(null);
            resetForm();
            setDialogTab(0);
            setPreviewValues({});
          }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isLoading || updateMutation.isLoading || !form.name.trim() || validSchema.length === 0 || isOffline}
          >
            {createMutation.isLoading || updateMutation.isLoading ? <CircularProgress size={20} /> : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={!!previewTemplate}
        onClose={() => { setPreviewTemplate(null); setStandalonePreviewValues({}); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Preview: {previewTemplate?.name}
          {(() => {
            const hasSchema = !!previewTemplate?.schema?.length;
            return (
              <>
                {hasSchema && (
                  <Chip label={`${previewTemplate!.schema!.length} fields`} size="small" sx={{ ml: 1 }} />
                )}
                {!hasSchema && previewTemplate && (
                  <Chip label="Legacy format" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                )}
              </>
            );
          })()}
        </DialogTitle>
        <DialogContent dividers>
          {previewTemplate && previewTemplate.schema && previewTemplate.schema.length > 0 ? (
            <TemplateFormRenderer
              schema={previewTemplate.schema as TemplateFieldControl[]}
              values={standalonePreviewValues}
              onChange={(key, val) => setStandalonePreviewValues(prev => ({ ...prev, [key]: val }))}
              treatmentPlan={planBlockPreview}
            />
          ) : previewTemplate ? (
            // Legacy templates have no schema — render the same 4-section layout the
            // clinical SOAP dialog uses, so the preview matches what vets actually see.
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This is a legacy template. It renders with the standard 4-section SOAP layout in the clinical workflow.
                Clone it to convert to the new structured-fields format.
              </Alert>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 3,
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">Subjective</Typography>
                  <SubjectiveSection data={{} as any} onChange={() => {}} disabled />
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">Objective</Typography>
                  <ObjectiveSection data={{} as any} onChange={() => {}} disabled />
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">Assessment</Typography>
                  <AssessmentSection data={{} as any} onChange={() => {}} disabled />
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">Plan</Typography>
                  <PlanSection data={{} as any} onChange={() => {}} disabled />
                </Box>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPreviewTemplate(null); setStandalonePreviewValues({}); }}>Close</Button>
          <Button variant="outlined" startIcon={<CloneIcon />} onClick={() => { handleClone(previewTemplate!); setPreviewTemplate(null); setStandalonePreviewValues({}); }}>
            Clone & Customize
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Generate Dialog */}
      <Dialog
        open={aiDialogOpen}
        onClose={() => { if (!docUploading && !aiGenerating) { setAiDialogOpen(false); resetAiDialog(); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AIIcon color="primary" />
            Generate Template with AI
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>

            <OfflineNotice sx={{ mb: 0 }}>
              AI template generation needs an internet connection.
            </OfflineNotice>

            {/* ── Drop Zone ──────────────────────────────────────────────── */}
            <Box
              onDragOver={e => { e.preventDefault(); setDocDragging(true); }}
              onDragLeave={() => setDocDragging(false)}
              onDrop={handleDocDrop}
              onClick={() => !docUploading && fileInputRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: docDragging ? 'primary.main' : docFile ? 'success.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: docUploading ? 'wait' : 'pointer',
                bgcolor: docDragging ? 'primary.50' : docFile ? 'success.50' : 'background.default',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
              }}
            >
              {docUploading ? (
                <Box>
                  <CircularProgress size={32} sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Analyzing document with AI…
                  </Typography>
                  <LinearProgress variant="determinate" value={docUploadProgress} sx={{ mt: 1, borderRadius: 1 }} />
                </Box>
              ) : docFile ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                  <CheckIcon color="success" sx={{ fontSize: 32 }} />
                  <Typography variant="body2" fontWeight={600}>{docFile.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to choose a different file
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <UploadIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                  <Typography variant="body2" fontWeight={600}>
                    Drop a template document here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF, PNG, JPG, WEBP, or DOCX — AI will reverse-engineer the fields
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<DocIcon />} sx={{ mt: 0.5 }}>
                    Browse file
                  </Button>
                </Box>
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".pdf,.png,.jpg,.jpeg,.webp,.docx"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleDocFileSelect(f); e.target.value = ''; }}
              />
            </Box>

            <Divider>
              <Chip label="or describe it" size="small" />
            </Divider>

            {/* ── Text description ────────────────────────────────────────── */}
            <Alert severity="info" sx={{ py: 0.5 }}>
              Describe the clinical exam and AI will generate a structured template schema.
            </Alert>
            <TextField
              label="Description *"
              fullWidth
              multiline
              rows={3}
              value={aiDescription}
              onChange={e => setAiDescription(e.target.value)}
              placeholder="e.g. A thorough colic evaluation including pain scoring, gut sounds in all quadrants, rectal exam, nasogastric tube findings, and treatment response tracking"
              disabled={docUploading}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Exam Type (optional)</InputLabel>
              <Select
                value={aiExamType}
                label="Exam Type (optional)"
                onChange={e => setAiExamType(e.target.value)}
                disabled={docUploading}
              >
                <MenuItem value=""><em>Auto-detect</em></MenuItem>
                {EXAM_TYPES.map(et => <MenuItem key={et.value} value={et.value}>{et.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAiDialogOpen(false); resetAiDialog(); }} disabled={docUploading || aiGenerating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={aiGenerating ? <CircularProgress size={18} /> : <AIIcon />}
            onClick={handleAIGenerate}
            disabled={!aiDescription.trim() || aiGenerating || docUploading || isOffline}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateEditor;
